import {
  IconButton,
  MenuItem,
  styledComponent,
  TextField,
  MenuList,
  Link,
  Autocomplete,
  Grid,
  Box,
  Paper,
  Typography,
  InputLabel,
  Checkbox,
  Select,
  Button,
  LocationOnIcon,
  Theme
} from '@hermes/web-components'

export const whiteColor = '#FFF'
const borderStyle = '1px solid #D5DEF9'
const defaultHeight = '48px'
const hoverColor = '#EAEEFC'
const doubleHoverColor = '#D5DEF9'
const transparentColor = 'transparent'
const getGradientBackground = ({
  leftTabHovered = false,
  rightTabHovered = false
}: {
  leftTabHovered?: boolean
  rightTabHovered?: boolean
}) =>
  `linear-gradient(90deg, ${leftTabHovered ? doubleHoverColor : hoverColor} 50%, ${
    rightTabHovered ? doubleHoverColor : hoverColor
  } 50%)`
const getHoverState = ({
  doubleHoverCondition = false,
  transparentHoverCondition = false,
  hoverCondition = true
}: {
  doubleHoverCondition?: boolean
  transparentHoverCondition?: boolean
  hoverCondition?: boolean
}) => {
  if (doubleHoverCondition) {
    return doubleHoverColor
  }

  if (transparentHoverCondition) {
    return transparentColor
  }

  if (hoverCondition) {
    return hoverColor
  }
}

export const SearchBarContainer = styledComponent(Grid, {
  shouldForwardProp: (prop: string) => !['isAnyOptionSelected', 'leftTabHovered', 'rightTabHovered'].includes(prop)
})<{ isAnyOptionSelected?: boolean; leftTabHovered?: boolean; rightTabHovered?: boolean }>(
  ({ isAnyOptionSelected, leftTabHovered, rightTabHovered }) => ({
    width: '90%',
    height: '50px',
    border: borderStyle,
    borderRadius: '32px',
    background: isAnyOptionSelected ? getGradientBackground({ leftTabHovered, rightTabHovered }) : whiteColor
  })
)

export const StyledIconButton = styledComponent(IconButton)({
  cursor: 'pointer',
  width: '100%',
  borderRadius: '32px',
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  padding: 0,
  margin: 0,
  '&.MuiIconButton-root': {
    backgroundColor: transparentColor
  },
  '&:hover, &.MuiIconButton': {
    background: `${transparentColor} !important`
  }
})

export const CustomMenuItem = styledComponent(MenuItem, {
  shouldForwardProp: (prop: string) =>
    !['isSearchFieldFocused', 'openSearchTypeDropdown', 'isLocationFieldFocused'].includes(prop)
})<{ isSearchFieldFocused?: boolean; openSearchTypeDropdown?: boolean; isLocationFieldFocused?: boolean }>(
  ({ isSearchFieldFocused, openSearchTypeDropdown, isLocationFieldFocused }) => ({
    height: defaultHeight,
    width: '100%',
    padding: '16px',
    '&:hover, &:focus': {
      backgroundColor: openSearchTypeDropdown
        ? whiteColor
        : getHoverState({
            transparentHoverCondition: isSearchFieldFocused,
            doubleHoverCondition: isLocationFieldFocused
          }),
      borderRadius: '32px',
      borderWidth: 0
    }
  })
)

export const StyledAutocomplete = styledComponent(Autocomplete, {
  shouldForwardProp: (prop: string) => !['openSearchTypeDropdown'].includes(prop)
})<{
  openSearchTypeDropdown?: boolean
}>(({ openSearchTypeDropdown }) => ({
  height: defaultHeight,
  '&:hover, &:focus': {
    background: openSearchTypeDropdown
      ? `linear-gradient(90deg, ${doubleHoverColor} 50%, ${hoverColor} 50%)`
      : transparentColor
  }
}))

export const AutocompleteSearchTextField = styledComponent(TextField, {
  shouldForwardProp: (prop: string) =>
    ![
      'isBorderLeftHidden',
      'isBorderRightHidden',
      'isSearchFieldFocused',
      'openSearchTypeDropdown',
      'isLocationFieldFocused'
    ].includes(prop)
})<{
  isBorderLeftHidden?: boolean
  isBorderRightHidden?: boolean
  isSearchFieldFocused?: boolean
  openSearchTypeDropdown?: boolean
  isLocationFieldFocused?: boolean
}>(
  ({
    isBorderLeftHidden,
    isBorderRightHidden,
    isSearchFieldFocused,
    openSearchTypeDropdown,
    isLocationFieldFocused
  }) => ({
    height: defaultHeight,
    borderRadius: isSearchFieldFocused ? '32px' : 0,
    borderRight: isBorderRightHidden ? '' : borderStyle,
    borderLeft: isBorderLeftHidden ? '' : borderStyle,
    backgroundColor: isSearchFieldFocused ? whiteColor : transparentColor,
    borderWidth: isSearchFieldFocused ? 0 : 1,
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&:hover, &:focus': {
      height: defaultHeight,
      backgroundColor: isSearchFieldFocused
        ? whiteColor
        : getHoverState({ doubleHoverCondition: openSearchTypeDropdown || isLocationFieldFocused }),
      borderRadius: '32px',
      borderWidth: 0
    }
  })
)

export const LocationSearchTextField = styledComponent(TextField, {
  shouldForwardProp: (prop: string) => !['isAnyOptionSelected', 'isLocationFieldFocused'].includes(prop)
})<{ isAnyOptionSelected?: boolean; isLocationFieldFocused?: boolean }>(
  ({ isAnyOptionSelected, isLocationFieldFocused }) => ({
    height: defaultHeight,
    borderTopRightRadius: '32px',
    borderBottomRightRadius: '32px',
    borderWidth: 0,
    backgroundColor: isLocationFieldFocused ? whiteColor : transparentColor,
    borderRadius: '32px',
    '.MuiInputBase-root': {
      padding: '0 85px 0 0',
      input: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
      }
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '&:hover, &:focus': {
      height: defaultHeight,
      backgroundColor: isLocationFieldFocused
        ? whiteColor
        : getHoverState({ doubleHoverCondition: isAnyOptionSelected }),
      borderWidth: 0
    }
  })
)

export const StyledMenuList = styledComponent(MenuList, {
  shouldForwardProp: (prop: string) => !['isOpen'].includes(prop)
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  width: '100%',
  border: borderStyle,
  borderRadius: '16px',
  display: isOpen ? 'block' : 'none',
  position: 'absolute',
  boxShadow: '0px 11px 15px 0px #0203030A, 0px 9px 46px 0px #02030305, 0px 24px 38px 0px #02030308',
  top: '110%'
}))

export const StyledLink = styledComponent(Link)({
  textDecoration: 'none',
  textDecorationColor: '-moz-initial',
  color: 'text.primary'
})

export const TypeMenuItem = styledComponent(MenuItem, {
  shouldForwardProp: (prop: string) => !['isFirstItem', 'isLastItem'].includes(prop)
})<{ isFirstItem?: boolean; isLastItem?: boolean; theme?: Theme }>(({ isFirstItem, isLastItem, theme }) => ({
  backgroundColor: '#fff',
  borderTopLeftRadius: isFirstItem ? '16px' : 0,
  borderTopRightRadius: isFirstItem ? '16px' : 0,
  borderBottomLeftRadius: isLastItem ? '16px' : 0,
  borderBottomRightRadius: isLastItem ? '16px' : 0,
  width: '100%',
  padding: '12px',
  '&:hover': {
    backgroundColor: theme.custom.SearchBar.MenuItem.backgroundColor
  }
}))

export const SearchButtonContainer = styledComponent(Box)({
  paddingLeft: '8px',
  justifyContent: 'center',
  alignContent: 'center'
})

export const StyledSearchIconButton = styledComponent(IconButton)({
  '&.MuiIconButton-root': {
    backgroundColor: '#1A3586'
  }
})

export const StyledPaper = styledComponent(Paper, {
  shouldForwardProp: (prop: string) => !['open'].includes(prop)
})<{
  open: boolean
}>(({ open }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  border: borderStyle,
  borderRadius: '16px',
  display: open ? 'flex' : 'none',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px 0px',
  maxHeight: '40vh',
  overflowY: 'auto',
  marginTop: '52px'
}))

export const StyledNoOptionText = styledComponent(Typography)({
  width: '100%',
  color: 'rgb(76, 88, 112)',
  padding: '6px 16px'
})

export const StyledCheckbox = styledComponent(Checkbox)(({ checked }) => ({
  '& .MuiSvgIcon-root': {
    fontSize: '24px !important'
  },
  '&.MuiCheckbox-root': {
    padding: '0 4px 0 0',
    color: checked ? '#406AE3' : '#8794AE'
  }
}))

export const StyledSelect = styledComponent(Select)(({ theme }) => ({
  borderRadius: '32px',
  border: '1px solid #D5DEF9',
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '& .MuiList-root': {
    '&::-webkit-scrollbar': {
      width: 8,
      borderRadius: 8,
      background: '#D5DEF9'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.custom.scrollbarThumb.backgroundColor,
      borderRadius: 8
    }
  }
}))

export const StyledMenuItem = styledComponent(MenuItem)(({ theme }) => ({
  '& .MuiMenuItem-root': {
    borderWidth: 0,
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13.33px',
    fontFamily: theme.typography.fontFamily,
    flexDirection: 'row',
    alignItems: 'center',
    height: '36px !important',
    borderRadius: 0,
    flex: 'none',
    order: 0,
    alignSelf: 'stretch',
    flexGrow: 0
  },
  '&:hover': theme?.custom?.SearchFilters?.menuItemHover ?? {}
}))

export const StyledFilterButton = styledComponent(Button)({
  borderRadius: '24px',
  border: '1px solid #142F76',
  color: '#142F76',
  fontWeight: 800,
  '&:active, &:focus, &:hover': {
    backgroundColor: 'transparent',
    color: '#142F76'
  }
})

export const StyledInputLabel = styledComponent(InputLabel)(({ theme }) => ({
  ...(theme?.custom?.SearchFilters?.inputLabel ?? {})
}))

export const StyledLocationOnIcon = styledComponent(LocationOnIcon)({
  width: '16px',
  height: '16px'
})
